import { useCallback, useEffect, useMemo, useState } from "react";
import PortalLayout from "../../components/PortalLayout";
import { api, formatApiErrorDetail, formatCurrencyEUR, formatDate, formatNumber } from "../../lib/api";

export default function AdminDepositsPage() {
  const [users, setUsers] = useState([]);
  const [deposits, setDeposits] = useState([]);
  const [error, setError] = useState("");
  const [form, setForm] = useState({
    user_id: "",
    principal: "",
    annual_rate_pct: "",
    mgmt_fee_pct: "",
    perf_fee_pct: "",
    duration_months: "",
    start_date: new Date().toISOString().slice(0, 10),
    auto_reinvest: false,
    label: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(
    () =>
      Promise.all([api.get("/admin/users"), api.get("/admin/deposits")])
        .then(([u, d]) => {
          setUsers(u.data.filter((x) => x.role === "client"));
          setDeposits(d.data);
        })
        .catch((e) => setError(formatApiErrorDetail(e.response?.data?.detail) || e.message)),
    []
  );

  useEffect(() => {
    load();
  }, [load]);

  const userMap = useMemo(() => {
    const m = {};
    users.forEach((u) => (m[u.id] = u));
    return m;
  }, [users]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await api.post("/admin/deposits", {
        ...form,
        principal: Number(form.principal),
        annual_rate_pct: Number(form.annual_rate_pct),
        mgmt_fee_pct: Number(form.mgmt_fee_pct || 0),
        perf_fee_pct: Number(form.perf_fee_pct || 0),
        duration_months: Number(form.duration_months),
      });
      setForm({ ...form, principal: "", annual_rate_pct: "", mgmt_fee_pct: "", perf_fee_pct: "", duration_months: "", label: "" });
      load();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Eliminare questo deposito?")) return;
    try {
      await api.delete(`/admin/deposits/${id}`);
      load();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  return (
    <PortalLayout>
      <section className="max-w-[1280px] mx-auto px-6 sm:px-8 pt-14 pb-8" data-testid="admin-deposits-page">
        <div className="br-fade-up">
          <div className="br-overline">Depositi</div>
          <h1 className="font-serif-display text-[clamp(2.4rem,4.5vw,3.6rem)] mt-3">
            Gestione <em>vincoli</em>.
          </h1>
          <p className="text-[1.05rem] text-[color:var(--br-text)] mt-3 max-w-[640px]">
            Maturazione lineare giornaliera: <span className="font-mono-fin text-[color:var(--br-bronze)]">capitale × tasso/365</span>. Nessuna capitalizzazione interna.
          </p>
        </div>

        <div className="mt-10 grid lg:grid-cols-5 gap-8 br-fade-up br-delay-1">
          <form onSubmit={submit} className="br-card p-7 space-y-4 lg:col-span-2" data-testid="admin-deposit-form">
            <h2 className="font-serif-display text-[1.6rem]">Nuovo deposito</h2>
            <label className="block">
              <span className="br-overline block mb-2">Cliente</span>
              <select
                className="br-input"
                style={{ background: "var(--br-bg-2)" }}
                value={form.user_id}
                onChange={(e) => setForm({ ...form, user_id: e.target.value })}
                required
                data-testid="deposit-form-user"
              >
                <option value="">— seleziona —</option>
                {users.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.name} · {u.email}
                  </option>
                ))}
              </select>
            </label>

            <div className="grid grid-cols-2 gap-4">
              <label>
                <span className="br-overline block mb-2">Capitale (EUR)</span>
                <input
                  className="br-input"
                  type="number"
                  step="1"
                  min="1"
                  value={form.principal}
                  onChange={(e) => setForm({ ...form, principal: e.target.value })}
                  required
                  data-testid="deposit-form-principal"
                />
              </label>
              <label>
                <span className="br-overline block mb-2">Durata (mesi)</span>
                <input
                  className="br-input"
                  type="number"
                  step="1"
                  value={form.duration_months}
                  onChange={(e) => setForm({ ...form, duration_months: e.target.value })}
                  required
                  data-testid="deposit-form-duration"
                />
              </label>
              <label>
                <span className="br-overline block mb-2">Tasso annuo lordo %</span>
                <input
                  className="br-input"
                  type="number"
                  step="0.01"
                  min="0.01"
                  value={form.annual_rate_pct}
                  onChange={(e) => setForm({ ...form, annual_rate_pct: e.target.value })}
                  required
                  data-testid="deposit-form-rate"
                />
              </label>
              <label>
                <span className="br-overline block mb-2">Management fee %</span>
                <input
                  className="br-input"
                  type="number"
                  step="0.01"
                  min="0"
                  value={form.mgmt_fee_pct}
                  onChange={(e) => setForm({ ...form, mgmt_fee_pct: e.target.value })}
                  data-testid="deposit-form-fee"
                />
              </label>
              <label>
                <span className="br-overline block mb-2">Performance fee %</span>
                <input
                  className="br-input"
                  type="number"
                  step="0.01"
                  min="0"
                  max="99.99"
                  value={form.perf_fee_pct}
                  onChange={(e) => setForm({ ...form, perf_fee_pct: e.target.value })}
                  data-testid="deposit-form-perf-fee"
                />
              </label>
              <label>
                <span className="br-overline block mb-2">Decorrenza</span>
                <input
                  className="br-input"
                  type="date"
                  value={form.start_date}
                  onChange={(e) => setForm({ ...form, start_date: e.target.value })}
                  required
                  data-testid="deposit-form-start"
                />
              </label>
              <label className="flex items-center gap-3 pt-7">
                <input
                  type="checkbox"
                  checked={form.auto_reinvest}
                  onChange={(e) => setForm({ ...form, auto_reinvest: e.target.checked })}
                  data-testid="deposit-form-reinvest"
                />
                <span className="font-mono-fin text-[0.7rem] tracking-[0.18em] uppercase">Auto-reinvest</span>
              </label>
            </div>

            <label className="block">
              <span className="br-overline block mb-2">Etichetta</span>
              <input
                className="br-input"
                value={form.label}
                onChange={(e) => setForm({ ...form, label: e.target.value })}
                placeholder="es. Gold — 24m vincolato"
                data-testid="deposit-form-label"
              />
            </label>

            <YieldPreview form={form} />

            {error && (
              <div className="text-[0.85rem] py-3 px-4 border" style={{ borderColor: "var(--br-red)", color: "var(--br-red)", background: "rgba(179,107,107,0.06)" }} data-testid="deposit-form-error">
                {error}
              </div>
            )}

            <button type="submit" className="br-btn-primary w-full" disabled={submitting} data-testid="deposit-form-submit">
              {submitting ? "Apertura…" : "Apri deposito"}
            </button>
          </form>

          <div className="lg:col-span-3 br-card overflow-x-auto" data-testid="admin-deposits-table">
            <table className="w-full text-left">
              <thead>
                <tr style={{ borderBottom: "1px solid var(--br-line)" }}>
                  <Th>Cliente</Th>
                  <Th>Etichetta</Th>
                  <Th align="right">Capitale</Th>
                  <Th align="right">Tasso</Th>
                  <Th align="right">Mgmt · Perf fee</Th>
                  <Th align="right">Maturato</Th>
                  <Th align="right">€/giorno</Th>
                  <Th align="right">Durata</Th>
                  <Th>Decorrenza</Th>
                  <Th>Stato</Th>
                  <Th align="right">Azioni</Th>
                </tr>
              </thead>
              <tbody>
                {deposits.length === 0 ? (
                  <tr>
                    <td colSpan={11} className="px-5 py-8 text-center text-[color:var(--br-text-dim)]">
                      Nessun deposito ancora aperto.
                    </td>
                  </tr>
                ) : (
                  deposits.map((d) => (
                    <tr key={d.id} className="border-b" style={{ borderColor: "var(--br-line)" }} data-testid={`deposit-row-${d.id}`}>
                      <Td>
                        <div className="text-[color:var(--br-parchment)]">{userMap[d.user_id]?.name || "—"}</div>
                        <div className="text-[0.72rem] font-mono-fin text-[color:var(--br-text-dim)]">{userMap[d.user_id]?.email}</div>
                      </Td>
                      <Td className="font-serif-display italic text-[1rem] text-[color:var(--br-parchment)]">{d.label || `Dep ${d.id.slice(-6)}`}</Td>
                      <Td align="right" className="font-mono-fin">{formatCurrencyEUR(d.principal)}</Td>
                      <Td align="right" className="font-mono-fin" style={{ color: "var(--br-bronze)" }}>{formatNumber(d.annual_rate_pct, 2)}%</Td>
                      <Td align="right" className="font-mono-fin" data-testid={`deposit-fees-${d.id}`}>
                        <div className="text-[0.8rem] text-[color:var(--br-parchment)]">{formatCurrencyEUR(d.mgmt_fee_annual_eur)}<span className="text-[0.66rem] text-[color:var(--br-text-dim)]">/anno</span></div>
                        <div className="text-[0.72rem]" style={{ color: "var(--br-red)" }}>− {formatCurrencyEUR(d.perf_fee_accrued)} ({formatNumber(d.perf_fee_pct, 1)}%)</div>
                      </Td>
                      <Td align="right" className="font-mono-fin" style={{ color: "var(--br-bronze-soft)" }} data-testid={`deposit-accrued-${d.id}`}>
                        + {formatCurrencyEUR(d.accrued_net)}
                      </Td>
                      <Td align="right" className="font-mono-fin" data-testid={`deposit-daily-${d.id}`}>{formatCurrencyEUR(d.daily_net)}</Td>
                      <Td align="right" className="font-mono-fin">{d.duration_months}m</Td>
                      <Td className="font-mono-fin text-[color:var(--br-text-dim)]">{formatDate(d.start_date)}</Td>
                      <Td className="font-mono-fin text-[0.7rem] uppercase tracking-[0.16em]">{d.status}</Td>
                      <Td align="right">
                        <button
                          onClick={() => remove(d.id)}
                          className="br-btn-outline"
                          style={{ padding: "6px 12px", fontSize: "0.66rem", borderColor: "var(--br-red)", color: "var(--br-red)" }}
                          data-testid={`delete-deposit-${d.id}`}
                        >
                          Elimina
                        </button>
                      </Td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </section>
    </PortalLayout>
  );
}

function YieldPreview({ form }) {
  const principal = Number(form.principal) || 0;
  const rate = Number(form.annual_rate_pct) || 0;
  const fee = Number(form.mgmt_fee_pct) || 0;
  const perf = Number(form.perf_fee_pct) || 0;
  if (!principal || !rate) return null;
  const netRate = rate * (1 - perf / 100) - fee;
  if (netRate <= 0) {
    return (
      <div
        className="text-[0.85rem] py-3 px-4 border"
        style={{ borderColor: "var(--br-red)", color: "var(--br-red)", background: "rgba(179,107,107,0.06)" }}
        data-testid="deposit-preview-warning"
      >
        Attenzione: le commissioni superano il tasso — il rendimento netto del cliente sarà 0,00 €.
      </div>
    );
  }
  const grossDaily = (principal * rate) / 100 / 365;
  const mgmtAnnual = (principal * fee) / 100;
  const perfDaily = grossDaily * (perf / 100);
  const netDaily = grossDaily - perfDaily - mgmtAnnual / 365;
  return (
    <div className="border p-4" style={{ borderColor: "var(--br-line-bright)", background: "var(--br-bg-2)" }} data-testid="deposit-preview">
      <div className="br-overline mb-3">Anteprima rendimento netto ({formatNumber(netRate, 2)}%)</div>
      <div className="grid grid-cols-3 gap-3 font-mono-fin text-[0.85rem]">
        <div>
          <div className="text-[0.62rem] tracking-[0.18em] uppercase text-[color:var(--br-text-dim)]">Giorno</div>
          <div className="mt-1" style={{ color: "var(--br-bronze)" }} data-testid="preview-daily">{formatCurrencyEUR(netDaily)}</div>
        </div>
        <div>
          <div className="text-[0.62rem] tracking-[0.18em] uppercase text-[color:var(--br-text-dim)]">Mese (30g)</div>
          <div className="mt-1" style={{ color: "var(--br-bronze)" }} data-testid="preview-monthly">{formatCurrencyEUR(netDaily * 30)}</div>
        </div>
        <div>
          <div className="text-[0.62rem] tracking-[0.18em] uppercase text-[color:var(--br-text-dim)]">Anno</div>
          <div className="mt-1" style={{ color: "var(--br-bronze)" }} data-testid="preview-yearly">{formatCurrencyEUR(netDaily * 365)}</div>
        </div>
      </div>
      <div className="mt-3 pt-3 grid grid-cols-2 gap-3 font-mono-fin text-[0.8rem]" style={{ borderTop: "1px solid var(--br-line)" }}>
        <div>
          <div className="text-[0.62rem] tracking-[0.18em] uppercase text-[color:var(--br-text-dim)]">Mgmt fee · somma fissa</div>
          <div className="mt-1 text-[color:var(--br-parchment)]" data-testid="preview-mgmt-fee">{formatCurrencyEUR(mgmtAnnual)}/anno</div>
        </div>
        <div>
          <div className="text-[0.62rem] tracking-[0.18em] uppercase text-[color:var(--br-text-dim)]">Perf. fee · dinamica</div>
          <div className="mt-1" style={{ color: "var(--br-red)" }} data-testid="preview-perf-fee">− {formatCurrencyEUR(perfDaily)}/giorno</div>
        </div>
      </div>
    </div>
  );
}

function Th({ children, align = "left" }) {
  return <th className="px-5 py-4 font-mono-fin text-[0.62rem] tracking-[0.22em] uppercase font-medium" style={{ color: "var(--br-text-dim)", textAlign: align }}>{children}</th>;
}
function Td({ children, align = "left", className = "", style, ...rest }) {
  return <td className={`px-5 py-4 text-[0.9rem] ${className}`} style={{ textAlign: align, ...style }} {...rest}>{children}</td>;
}
